import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { useApiData } from 'lib/api';
import { LoadingIndicator, ModalButtonWithForm } from 'components/common';
import { Checkbox, FormGroup, Select } from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { GetCalendarEventAvailableAchievementsRes } from 'shared-types/responses';

type FormState = {
  achievementId: number;
  propagateAchievement: boolean;
  userAttributes?: Record<string, any>;
};

interface Props {
  updateAchievements: () => Promise<void>;
}

const CalendarEventAchievementsAdd = ({ updateAchievements }: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const [fetchOptions, setFetchOptions] = useState(false);

  const { data, loading } =
    useApiData<GetCalendarEventAvailableAchievementsRes>({
      url: `/admin/calendar-events/${calendarEvent.id}/achievements-available`,
      onlyIf: fetchOptions
    });

  const handleSubmit = async (values: FormState) => {
    const data = { ...values };
    if (data.userAttributes) {
      data.userAttributes = Object.fromEntries(
        Object.entries(data.userAttributes).map(([key, value]) => [
          key.split('-')[1],
          value
        ])
      );
    }

    await api.post(
      `/admin/calendar-events/${calendarEvent.id}/achievements`,
      data
    );
    await updateAchievements();

    dispatch(
      actions.addNotice({
        text: messages.achievementUsersAddSuccess,
        type: 'success'
      })
    );
  };

  return !calendarEvent ? null : (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonText={messages.eventCalendarLinkAchievement}
      modalActionText={messages.commonAdd}
      modalTitle={messages.eventCalendarLinkAchievement}
      onHide={() => setFetchOptions(false)}
      onShow={() => setFetchOptions(true)}
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {({ values }: { values: FormState }) =>
        loading ? (
          <LoadingIndicator />
        ) : (
          <>
            <FormGroup label={messages.achievement}>
              <Select
                field="achievementId"
                id="achievement-id"
                isSearchable={true}
                noOptionsMessage={messages.achievementsEmpty}
                options={
                  data?.achievements.map(({ id, title }) => ({
                    value: id,
                    label: title
                  })) || []
                }
                placeholder={messages.eventCalendarAchievementSelect}
              />
            </FormGroup>

            {values.achievementId &&
              data?.achievements
                .find((achievement) => achievement.id === values.achievementId)
                ?.userAttributes.map((attribute) => (
                  <FormGroup
                    label={
                      attribute.titleTranslations?.[language] || attribute.title
                    }
                    key={attribute.id}
                  >
                    <ProductUserCustomAttributeRenderFormField
                      attribute={attribute}
                      fieldParent="userAttributes"
                    />
                  </FormGroup>
                ))}

            <Checkbox
              disabled={!values.achievementId}
              id="propagate-achievement"
              field="propagateAchievement"
              label={messages.eventCalendarAchievementPropagate}
              style={{ marginTop: 20 }}
            />
          </>
        )
      }
    </ModalButtonWithForm>
  );
};

export default CalendarEventAchievementsAdd;
